import { apiClient } from './Api';
import { VOUCHER_ROUTE_API } from '../constants';

export const ACTIONS = { ACTIVATE: 'activate', DEACTIVATE: 'deactivate', DELETE: 'delete' };

function unwrap(res) {
  const d = res && res.data ? res.data : {};
  return d.data !== undefined ? d.data : d;
}

function groupsOf(v) {
  return String(v.group_name || '').split(',').map((g) => g.trim()).filter(Boolean);
}

export async function fetchVouchersByGroup(group) {
  const target = String(group || '').trim();
  const matched = [];
  let page = 1;
  let lastPage = 1;
  do {
    // eslint-disable-next-line no-await-in-loop
    const res = await apiClient().get(VOUCHER_ROUTE_API, { params: { per_page: 100, page } });
    const body = unwrap(res);
    const items = body.data || body || [];
    items.forEach((v) => { if (groupsOf(v).includes(target)) matched.push(v); });
    lastPage = (body.meta && body.meta.last_page) || body.last_page || page;
    page += 1;
  } while (page <= lastPage);
  return matched;
}

export async function fetchUsage(id) {
  try {
    const res = await apiClient().get(`${VOUCHER_ROUTE_API}/${id}/usages`);
    const body = unwrap(res);
    return {
      usages: Number(body.usages || 0),
      transaction_usages: Number(body.transaction_usages || 0),
    };
  } catch (e) {
    return { usages: 0, transaction_usages: 0, error: true };
  }
}

const normTime = (t) => {
  const m = String(t || '').match(/^(\d{2}:\d{2})/);
  return m ? `${m[1]}:00` : '00:00:00';
};
// Faithful echo of the fields AdminVoucherController::update reads, mirroring the
// single-edit screen (FormCard), with only `active` changed. The update endpoint
// is a full overwrite, so every field must be sent back as-is.
export function voucherToUpdatePayload(v, activeTarget) {
  return {
    code: v.code,
    name: v.name,
    description: v.description,
    deduct_type: v.deduct_type,
    voucher_type: v.voucher_type,
    type: v.type,
    amount: v.amount != null ? parseInt(v.amount, 10) : v.amount,
    minimum_purchase: v.minimum_purchase === '' || v.minimum_purchase == null ? 0 : parseInt(v.minimum_purchase, 10),
    max_amount: v.max_amount === '' || v.max_amount == 0 || v.max_amount == null ? null : parseInt(v.max_amount, 10),
    limit: v.limit == null ? null : v.limit,
    limit_per_user: v.limit_per_user,
    daily_limit: v.daily_limit && v.daily_limit > 0 ? v.daily_limit : null,
    card_limit: v.card_limit,
    platform_specifications: v.platform_specifications ? v.platform_specifications : null,
    domain: v.domain || '',
    group_name: v.group_name || null,
    daily_start_time: normTime(v.daily_start_time),
    started_at: v.new_user_only ? v.new_user_started_at : v.started_at,
    ended_at: v.new_user_only ? v.new_user_ended_at : v.ended_at,
    auto_apply: !!v.auto_apply,
    is_cashback: !!v.is_cashback,
    disposable_voucher: !!v.disposable_voucher,
    listing_pre_order: !!v.listing_pre_order,
    listing_pre_verified: !!v.listing_pre_verified,
    listing_standard: !!v.listing_standard,
    new_user_only: !!v.new_user_only,
    franchise: v.franchise || 'kard_avenue',
    active: activeTarget,
  };
}

// Builds the dry-run rows. For delete, fetches usage so used vouchers can be blocked.
export async function buildPreview(vouchers, action, onProgress) {
  const rows = [];
  for (let i = 0; i < vouchers.length; i += 1) {
    const v = vouchers[i];
    const row = {
      id: v.id, code: v.code, name: v.name,
      currentActive: v.active ? 1 : 0,
      usages: null, txUsages: null, blocked: false, blockReason: '',
    };
    if (action === ACTIONS.ACTIVATE && v.active) { row.blocked = true; row.blockReason = 'already active'; }
    if (action === ACTIONS.DEACTIVATE && !v.active) { row.blocked = true; row.blockReason = 'already inactive'; }
    if (action === ACTIONS.DELETE) {
      // eslint-disable-next-line no-await-in-loop
      const u = await fetchUsage(v.id);
      row.usages = u.usages;
      row.txUsages = u.transaction_usages;
      if (u.error) { row.blocked = true; row.blockReason = 'usage check failed, blocked for safety'; }
      else if (u.transaction_usages > 0) { row.blocked = true; row.blockReason = `used in ${u.transaction_usages} transaction(s)`; }
    }
    rows.push(row);
    if (onProgress) onProgress(i + 1, vouchers.length);
  }
  return rows;
}

// Executes against the non-blocked rows. Delete is irreversible, no rollback.
export async function runManage(vouchers, previewRows, action, onProgress) {
  const byId = {};
  vouchers.forEach((v) => { byId[v.id] = v; });
  const targets = previewRows.filter((r) => !r.blocked);
  const results = [];
  for (let i = 0; i < targets.length; i += 1) {
    const r = targets[i];
    const result = { id: r.id, code: r.code, status: 'ok' };
    try {
      if (action === ACTIONS.DELETE) {
        // eslint-disable-next-line no-await-in-loop
        await apiClient().delete(`${VOUCHER_ROUTE_API}/${r.id}`);
      } else {
        const activeTarget = action === ACTIONS.ACTIVATE;
        // eslint-disable-next-line no-await-in-loop
        await apiClient().put(`${VOUCHER_ROUTE_API}/${r.id}`, voucherToUpdatePayload(byId[r.id], activeTarget));
      }
    } catch (err) {
      result.status = 'failed';
      const data = err && err.response && err.response.data;
      result.error = (data && (data.message || (data.error && data.error.message))) || (err && err.message) || 'request failed';
    }
    results.push(result);
    if (onProgress) onProgress(i + 1, targets.length, result);
    if (result.status === 'failed') break;
  }
  return results;
}
